import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { StorageService, StoredUser } from '../services/storage.service';
import { apiService } from '../services/api';

interface AuthState {
  user: StoredUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextType extends AuthState {
  login: (token: string, refreshToken: string, user: StoredUser) => void;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: StorageService.getUser(),
    token: StorageService.getToken(),
    isAuthenticated: !!StorageService.getToken(),
    isLoading: true,
  });

  const login = useCallback((token: string, refreshToken: string, user: StoredUser) => {
    StorageService.setToken(token);
    StorageService.setRefreshToken(refreshToken);
    StorageService.setUser(user);
    setState({
      user,
      token,
      isAuthenticated: true,
      isLoading: false,
    });
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiService.logout();
    } catch {
      // Ignore logout errors
    } finally {
      StorageService.clearAuth();
      setState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  }, []);

  const checkAuth = useCallback(async (): Promise<boolean> => {
    const token = StorageService.getToken();
    if (!token) {
      setState((prev) => ({ ...prev, isAuthenticated: false, isLoading: false }));
      return false;
    }

    try {
      const isValid = await apiService.verifyToken();
      if (isValid) {
        const user = StorageService.getUser();
        setState({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
        });
        return true;
      } else {
        StorageService.clearAuth();
        setState({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        });
        return false;
      }
    } catch {
      StorageService.clearAuth();
      setState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
      return false;
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <AuthContext.Provider value={{ ...state, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
